import React, { useEffect, useRef, useState } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";

const COIN_COUNT = 40;
const COIN_RADIUS = 220; // spread radius around origin
const COIN_PICKUP_RANGE = 2.5;

function spawnCoin(around = new THREE.Vector3()) {
  // Spawn at random point on a ring around a center
  const angle = Math.random() * Math.PI * 2;
  const dist = 40 + Math.random() * COIN_RADIUS;
  return {
    id: Math.random().toString(36).slice(2),
    x: around.x + Math.cos(angle) * dist,
    z: around.z + Math.sin(angle) * dist,
    y: 1.1,
  };
}

export default function CarScene({ onTelemetry, onCoinCollected, paused, carColor = "#06B6D4" }) {
  const carGroup = useRef();
  const wheelsRef = useRef([]);
  const coinRefs = useRef({});
  const cameraTargetRef = useRef(new THREE.Vector3());
  const { camera } = useThree();

  const [coins, setCoins] = useState(() => Array.from({ length: COIN_COUNT }, () => spawnCoin()));

  const stateRef = useRef({
    speed: 0,
    heading: 0,
    distance: 0,
    maxSpeed: 0,
    keys: {},
  });

  useEffect(() => {
    const down = (e) => { stateRef.current.keys[e.code] = true; };
    const up = (e) => { stateRef.current.keys[e.code] = false; };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
    };
  }, []);

  const propData = useRef(
    Array.from({ length: 60 }, (_, i) => ({
      x: (Math.random() - 0.5) * 400,
      z: (Math.random() - 0.5) * 400,
      type: Math.random() > 0.6 ? "cone" : (Math.random() > 0.5 ? "block" : "pillar"),
      color: ["#EAB308", "#06B6D4", "#EC4899", "#22C55E"][i % 4],
      scale: 0.6 + Math.random() * 1.2,
    }))
  );

  useFrame((_, dt) => {
    if (paused || !carGroup.current) return;
    const s = stateRef.current;
    const k = s.keys;

    const throttle = (k["KeyW"] || k["ArrowUp"]) ? 1 : 0;
    const brake = (k["KeyS"] || k["ArrowDown"]) ? 1 : 0;
    const left = (k["KeyA"] || k["ArrowLeft"]) ? 1 : 0;
    const right = (k["KeyD"] || k["ArrowRight"]) ? 1 : 0;

    const maxSpeedUnitsPerSec = 45;
    const acceleration = 18;
    const drag = 4;
    const brakeDecel = 30;

    s.speed += throttle * acceleration * dt;
    s.speed -= brake * brakeDecel * dt;
    s.speed -= drag * dt * Math.sign(s.speed);
    s.speed = THREE.MathUtils.clamp(s.speed, -12, maxSpeedUnitsPerSec);

    const steerInput = right - left;
    const speedFactor = Math.min(1, Math.abs(s.speed) / 8);
    const steerRate = 1.8 * speedFactor;
    s.heading -= steerInput * steerRate * dt * Math.sign(s.speed || 1);

    const dx = Math.sin(s.heading) * s.speed * dt;
    const dz = Math.cos(s.heading) * s.speed * dt;
    carGroup.current.position.x -= dx;
    carGroup.current.position.z -= dz;
    carGroup.current.rotation.y = s.heading;

    s.distance += Math.abs(s.speed * dt);
    s.maxSpeed = Math.max(s.maxSpeed, Math.abs(s.speed));

    const spin = s.speed * dt * 4;
    wheelsRef.current.forEach((w, i) => {
      if (!w) return;
      w.rotation.x += spin;
      if (i < 2) w.parent.rotation.y = -steerInput * 0.3;
    });

    // Chase camera
    const carPos = carGroup.current.position;
    const camOffset = new THREE.Vector3(
      -Math.sin(s.heading) * -8,
      4.5,
      -Math.cos(s.heading) * -8
    );
    const desired = carPos.clone().add(camOffset);
    camera.position.lerp(desired, 0.08);
    cameraTargetRef.current.lerp(carPos.clone().add(new THREE.Vector3(0, 1, 0)), 0.12);
    camera.lookAt(cameraTargetRef.current);

    // Rotate & bob coin meshes; check pickup
    let anyCollected = null;
    Object.entries(coinRefs.current).forEach(([id, m]) => {
      if (!m) return;
      m.rotation.y += dt * 2.5;
      m.position.y = 1.1 + Math.sin(performance.now() * 0.003 + m.position.x) * 0.15;
      const dxc = m.position.x - carPos.x;
      const dzc = m.position.z - carPos.z;
      if (dxc * dxc + dzc * dzc < COIN_PICKUP_RANGE * COIN_PICKUP_RANGE) {
        anyCollected = id;
      }
    });

    if (anyCollected) {
      setCoins((prev) => {
        const nextList = prev.filter(c => c.id !== anyCollected);
        // Respawn a new coin nearby to keep density
        nextList.push(spawnCoin(carPos));
        return nextList;
      });
      onCoinCollected?.();
    }

    onTelemetry({
      speed: Math.abs(s.speed) * 3.6,
      distance: s.distance,
      maxSpeed: s.maxSpeed * 3.6,
    });
  });

  return (
    <group>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]} receiveShadow>
        <planeGeometry args={[1000, 1000]} />
        <meshStandardMaterial color="#1a0f1a" roughness={0.95} metalness={0.05} />
      </mesh>
      <gridHelper args={[1000, 200, "#06B6D4", "#3f1d3f"]} position={[0, 0.01, 0]} />

      {[...Array(24)].map((_, i) => {
        const a = (i / 24) * Math.PI * 2;
        const r = 220;
        return (
          <mesh key={i} position={[Math.cos(a) * r, 4, Math.sin(a) * r]}>
            <coneGeometry args={[18 + (i % 3) * 4, 20 + (i % 5) * 4, 4]} />
            <meshStandardMaterial color="#170b1f" flatShading />
          </mesh>
        );
      })}

      {propData.current.map((p, i) => <Prop key={i} {...p} />)}

      {/* Coins */}
      {coins.map((c) => (
        <group key={c.id} position={[c.x, c.y, c.z]}>
          <mesh
            ref={(m) => { if (m) coinRefs.current[c.id] = m; else delete coinRefs.current[c.id]; }}
            rotation={[Math.PI / 2, 0, 0]}
            castShadow
          >
            <cylinderGeometry args={[0.5, 0.5, 0.12, 24]} />
            <meshStandardMaterial color="#EAB308" emissive="#EAB308" emissiveIntensity={1.2} metalness={0.8} roughness={0.25} />
          </mesh>
          <pointLight color="#EAB308" intensity={0.6} distance={4} />
        </group>
      ))}

      {/* Car */}
      <group ref={carGroup} position={[0, 0.4, 0]}>
        <mesh position={[0, 0.5, 0]} castShadow>
          <boxGeometry args={[1.6, 0.5, 3.6]} />
          <meshStandardMaterial color={carColor} metalness={0.8} roughness={0.25} emissive={carColor} emissiveIntensity={0.25} />
        </mesh>
        <mesh position={[0, 0.95, -0.2]} castShadow>
          <boxGeometry args={[1.3, 0.55, 1.7]} />
          <meshStandardMaterial color="#050505" metalness={0.7} roughness={0.2} />
        </mesh>
        <mesh position={[0.5, 0.55, -1.85]}>
          <boxGeometry args={[0.25, 0.15, 0.05]} />
          <meshStandardMaterial color="#EAB308" emissive="#EAB308" emissiveIntensity={2} />
        </mesh>
        <mesh position={[-0.5, 0.55, -1.85]}>
          <boxGeometry args={[0.25, 0.15, 0.05]} />
          <meshStandardMaterial color="#EAB308" emissive="#EAB308" emissiveIntensity={2} />
        </mesh>
        <mesh position={[0.5, 0.55, 1.85]}>
          <boxGeometry args={[0.25, 0.12, 0.05]} />
          <meshStandardMaterial color="#EC4899" emissive="#EC4899" emissiveIntensity={2} />
        </mesh>
        <mesh position={[-0.5, 0.55, 1.85]}>
          <boxGeometry args={[0.25, 0.12, 0.05]} />
          <meshStandardMaterial color="#EC4899" emissive="#EC4899" emissiveIntensity={2} />
        </mesh>

        <Wheel ref={(m) => (wheelsRef.current[0] = m)} position={[0.85, 0.3, -1.2]} />
        <Wheel ref={(m) => (wheelsRef.current[1] = m)} position={[-0.85, 0.3, -1.2]} />
        <Wheel ref={(m) => (wheelsRef.current[2] = m)} position={[0.85, 0.3, 1.2]} />
        <Wheel ref={(m) => (wheelsRef.current[3] = m)} position={[-0.85, 0.3, 1.2]} />
      </group>
    </group>
  );
}

const Wheel = React.forwardRef(function Wheel({ position }, ref) {
  return (
    <group position={position}>
      <mesh ref={ref} rotation={[0, 0, Math.PI / 2]} castShadow>
        <cylinderGeometry args={[0.35, 0.35, 0.3, 20]} />
        <meshStandardMaterial color="#0a0a0a" roughness={0.9} />
      </mesh>
    </group>
  );
});

function Prop({ x, z, type, color, scale }) {
  if (type === "cone") {
    return (
      <mesh position={[x, 0.5 * scale, z]} castShadow>
        <coneGeometry args={[0.4 * scale, 1 * scale, 12]} />
        <meshStandardMaterial color={color} emissive={color} emissiveIntensity={0.35} roughness={0.4} />
      </mesh>
    );
  }
  if (type === "pillar") {
    return (
      <mesh position={[x, 1.5 * scale, z]} castShadow>
        <boxGeometry args={[0.4 * scale, 3 * scale, 0.4 * scale]} />
        <meshStandardMaterial color="#111" emissive={color} emissiveIntensity={0.8} />
      </mesh>
    );
  }
  return (
    <mesh position={[x, 0.6 * scale, z]} castShadow>
      <boxGeometry args={[1.2 * scale, 1.2 * scale, 1.2 * scale]} />
      <meshStandardMaterial color={color} emissive={color} emissiveIntensity={0.2} metalness={0.3} roughness={0.6} />
    </mesh>
  );
}
